// Runtime language switching (docs/miniapp-m7-tz.md §3.5, acceptance #9).
//
// The ported i18n is a global: `t()` reads `window.__lang` at render time and
// `setLang()` mutates it — but mutating a global doesn't re-render React. This
// provider bridges that gap: it holds `lang` in state high in the tree, so
// `setLanguage()` both updates the global AND bumps state, cascading a re-render
// that re-evaluates every `t()` call in one tick — no reload, no remount.
//
// Persistence: the choice is written to CloudStorage (survives bot relaunch) and
// best-effort PATCHed to the server (so bot-side pushes can be localized too).
// First load: a saved CloudStorage choice wins; otherwise we guess from the
// Telegram user's language_code; otherwise 'ru'.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { setLang } from '../design'
import { apiRequest } from '../api/client'
import { getWebApp, hapticNotify, tgStorage } from '../telegram/tg'

export const LANGS = ['ru', 'uz'] as const
export type Lang = (typeof LANGS)[number]

const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as readonly string[]).includes(v)

/** Map a stored/raw value to a supported Lang. Legacy 'uzc' (Uzbek-Cyrillic,
 *  removed) folds into Latin 'uz'. Returns null for anything unrecognized. */
export function normalizeLang(v: unknown): Lang | null {
  if (v === 'uzc') return 'uz'
  return isLang(v) ? v : null
}

/** Sync best-guess from the Telegram client (CloudStorage overrides it async). */
function detectInitial(): Lang {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const q = normalizeLang(new URLSearchParams(window.location.search).get('lang'))
    if (q) return q
  }
  // A previously saved choice (synchronous localStorage mirror of CloudStorage)
  // wins from the very first paint — so the saved language shows on EVERY page
  // on load, not just after the async CloudStorage read lands.
  try {
    const saved = normalizeLang(localStorage.getItem('pt:lang'))
    if (saved) return saved
  } catch { /* private mode */ }
  const code = getWebApp()?.initDataUnsafe?.user?.language_code
  if (code === 'uz') return 'uz'
  return 'ru'
}

interface LangContextValue {
  lang: Lang
  setLanguage: (l: Lang) => void
}

const LangContext = createContext<LangContextValue | null>(null)

export function LangProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [lang, setLangState] = useState<Lang>(() => {
    const initial = detectInitial()
    setLang(initial) // window.__lang before first paint
    return initial
  })

  // First load: a persisted CloudStorage choice takes precedence over the
  // language_code guess (runs once).
  useEffect(() => {
    let alive = true
    void tgStorage
      .get('lang')
      .then((saved) => {
        const norm = normalizeLang(saved)
        if (alive && norm && norm !== window.__lang) {
          setLang(norm)
          setLangState(norm)
          // Anything already fetched with the guessed language must re-fetch so
          // server-localized AI prose matches the corrected language.
          void qc.invalidateQueries()
        }
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [qc])

  const setLanguage = useCallback((next: Lang) => {
    setLang(next) // global the ported t() reads
    setLangState(next) // re-render the tree → every t() re-evaluated this tick
    hapticNotify('success')
    void tgStorage.set('lang', next).catch(() => {})
    // AI prose is localized server-side per Accept-Language; invalidate so every
    // already-loaded analysis/report/objection re-fetches in the new language
    // (the request now carries the updated window.__lang).
    void qc.invalidateQueries()
    // Best-effort server persist; failure is non-fatal (CloudStorage is truth).
    void apiRequest('/api/miniapp/me/notification-prefs', {
      method: 'PATCH',
      body: { language: next },
    }).catch(() => {})
  }, [qc])

  return <LangContext.Provider value={{ lang, setLanguage }}>{children}</LangContext.Provider>
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useLang must be used inside <LangProvider>')
  return ctx
}
